import OnlineIndicator from "@features/presence/components/OnlineIndicator";
import { getProfileImage, useProfile } from "@shared/hooks/useProfile";
import { User, UserProfile } from "@shared/types/user";
import theme from "@shared/utils/themeColors";
import React, { useEffect, useMemo, useState } from "react";
import { ChatWindow, useChitChat } from "../context/ChitChatContext";
import { useUserMapStore } from "../store/userMapStore";
import { Conversation } from "../types";

interface MinimizedChatWindowProps {
  chatWindow: ChatWindow;
  conversations?: Conversation[];
  userProfiles?: Record<string, UserProfile>;
}

const MinimizedChatWindow: React.FC<MinimizedChatWindowProps> = ({
  chatWindow,
  conversations = [],
  userProfiles = {},
}) => {
  const { closeChatWindow, maximizeChatWindow } = useChitChat();
  const { user: currentUser } = useProfile();
  const { userMap, fetchUser, getUser } = useUserMapStore();
  const [otherUser, setOtherUser] = useState<User | null>(null);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    maximizeChatWindow(chatWindow.conversationId);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    closeChatWindow(chatWindow.conversationId);
  };

  const isGroup = chatWindow.conversationType === "group";
  const hasUnread = (chatWindow.unreadCount || 0) > 0;

  const conversation = conversations.find(
    (c) => c.id === chatWindow.conversationId,
  );

  const otherUserId = useMemo(() => {
    if (conversation?.type === "direct" && currentUser?.id) {
      return conversation.participants.find((id) => id !== currentUser.id);
    }
    return chatWindow.user?.id;
  }, [conversation, currentUser?.id, chatWindow.user?.id]);

  useEffect(() => {
    if (!isGroup && otherUserId) {
      const cachedUser = getUser(otherUserId);
      if (cachedUser) {
        setOtherUser(cachedUser);
      } else {
        fetchUser(otherUserId).then((user) => {
          if (user) setOtherUser(user);
        });
      }
    }
  }, [otherUserId, isGroup, fetchUser, getUser, userMap]);

  const displayName = useMemo(() => {
    if (isGroup) return chatWindow.title;
    if (otherUser?.profile?.name) return otherUser.profile.name;
    if (userProfiles[otherUserId || ""]?.name)
      return userProfiles[otherUserId || ""].name;
    return chatWindow.title || "User";
  }, [isGroup, otherUser, userProfiles, otherUserId, chatWindow.title]);

  const displayImage = useMemo(() => {
    if (isGroup) return null;
    if (otherUser?.profile) {
      return getProfileImage(otherUser.profile);
    }
    const profile = userProfiles[otherUserId || ""];
    if (profile) {
      return getProfileImage(profile);
    }
    return null;
  }, [isGroup, otherUser, userProfiles, otherUserId]);

  return (
    <div
      className={`flex items-center rounded-lg shadow-lg border border-gray-300 dark:border-gray-700 cursor-pointer transition-colors ${hasUnread
        ? "bg-amber-100 dark:bg-amber-900/50 hover:bg-amber-200 dark:hover:bg-amber-900/70"
        : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750"
        }`}
      style={{ width: "200px" }}
    >
      <button
        onClick={handleClick}
        className="flex items-center flex-1 min-w-0 p-2 gap-2"
      >
        {isGroup ? (
          <span
            className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0"
            style={{ backgroundColor: chatWindow.group?.color || "#6366f1" }}
          >
            <svg
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="white"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7 17l2-10m4 10l2-10M5 9h14M5 15h14"
              />
            </svg>
          </span>
        ) : (
          <div className="relative flex-shrink-0">
            <img
              src={
                displayImage ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  displayName,
                )}&background=random`
              }
              alt={displayName}
              className="w-8 h-8 rounded-full object-cover"
            />
            <OnlineIndicator
              userId={otherUserId}
              className="absolute bottom-0 right-0"
            />
          </div>
        )}
        <span className={`text-sm font-medium ${theme.text.primary} truncate`}>
          {displayName}
        </span>
        {hasUnread && (
          <span className="flex-shrink-0 bg-amber-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
            {chatWindow.unreadCount}
          </span>
        )}
      </button>
      <button
        onClick={handleClose}
        className={`p-2 ${theme.text.mutedHover} flex-shrink-0`}
        aria-label="Close chat"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};

export default MinimizedChatWindow;
